import { Request } from "./request";
import type {
  ApiResponse,
  DeviceType,
  MessageContact,
  MessageHistoryPage,
  Platform,
  QrCodeResult,
  SubscriptionRecord,
  Task,
  WechatAccount,
  WechatAccountDetail,
  WechatChatroomMember,
  WechatLoginResult,
} from "./models";

const httpGet = <T>(url: string) =>
  Request.axiosInstance.get<ApiResponse<T>>(url).then((res) => res.data);

const httpPost = <T>(url: string, payload: unknown) =>
  Request.axiosInstance.post<ApiResponse<T>>(url, payload).then((res) => res.data);

const httpDelete = <T>(url: string) =>
  Request.axiosInstance.delete<ApiResponse<T>>(url).then((res) => res.data);

export const getPlatforms = (): Promise<ApiResponse<Platform[]>> =>
  httpGet<Platform[]>("/platform/list");

export const getQrCode = (
  platformId: string,
  wcId: string,
  device: string,
  area: string
): Promise<ApiResponse<QrCodeResult>> => {
  const params = new URLSearchParams({ wcId, deviceType: device, area });
  return httpGet<QrCodeResult>(
    `/platform/qrcode/${encodeURIComponent(platformId)}?${params.toString()}`
  );
};

export const wechatLogin = (
  platformId: string,
  wId: string,
  verifyCode: string,
  area: string,
  deviceType?: DeviceType
): Promise<ApiResponse<WechatLoginResult>> => {
  const requestData: Record<string, string> = { platformId, wId, area };
  if (verifyCode) requestData.verifyCode = verifyCode;
  if (deviceType) requestData.deviceType = deviceType;
  return httpPost<WechatLoginResult>("/wechat/login", requestData);
};

export const wechatLoginWithWcId = (wcId: string): Promise<ApiResponse<WechatLoginResult>> =>
  httpPost<WechatLoginResult>("/wechat/login", { wcId });

// -- permissions / user management -------------------------------------------
export interface MeInfo {
  username: string | null;
  isAdmin: boolean;
  canCreateAccount: boolean;
  accountCount: number; // number of WeChat accounts assigned to this user
}
export interface ManagedUser {
  username: string;
  role?: "admin" | "user";
  canCreateAccount?: boolean;
  createdAt?: string;
}

export const getMe = (): Promise<ApiResponse<MeInfo>> => httpGet<MeInfo>("/user/me");

export const getUsers = (): Promise<ApiResponse<ManagedUser[]>> => httpGet<ManagedUser[]>("/user/list");

export const saveUser = (
  username: string,
  fields: { role?: "admin" | "user"; canCreateAccount?: boolean },
): Promise<ApiResponse<string>> =>
  Request.axiosInstance.put<ApiResponse<string>>(`/user/${encodeURIComponent(username)}`, fields).then((res) => res.data);

export const removeUser = (username: string): Promise<ApiResponse<string>> =>
  httpDelete<string>(`/user/${encodeURIComponent(username)}`);

export const getUserAccounts = (username: string): Promise<ApiResponse<string[]>> =>
  httpGet<string[]>(`/user/${encodeURIComponent(username)}/accounts`);

export const saveUserAccounts = (username: string, wcIds: string[]): Promise<ApiResponse<string>> =>
  Request.axiosInstance
    .put<ApiResponse<string>>(`/user/${encodeURIComponent(username)}/accounts`, { wcIds })
    .then((res) => res.data);

// Web-based completion of the risk-control 扫码图形验证. `url` is the bare string
// /wechat/login returns when extra verification is required (an asilu/o0.je link).
// The gateway decrypts + drives it headless; the operator solves the slider over
// the WebSocket. On any failure the caller falls back to the manual QR (UrlDialog).
export const startWebVerify = (url: string): Promise<ApiResponse<{ sessionId: string }>> =>
  httpPost<{ sessionId: string }>("/wechat/web-verify/start", { url });

export const stopWebVerify = (sessionId: string): Promise<ApiResponse<string>> =>
  httpPost<string>("/wechat/web-verify/stop", { sessionId });

export interface WebVerifyFrame {
  state: "loading" | "ready" | "success" | "failed" | "expired";
  message: string;
  seq: number;
  w: number;
  h: number;
  frame?: string; // base64 jpeg, omitted when unchanged since `seq`
}

// Poll the latest screencast frame + session state. Plain HTTP so it traverses
// the existing /api reverse proxy (no WebSocket upgrade needed).
export const getWebVerifyFrame = (sessionId: string, seq: number): Promise<ApiResponse<WebVerifyFrame>> =>
  httpGet<WebVerifyFrame>(`/wechat/web-verify/frame?sessionId=${encodeURIComponent(sessionId)}&seq=${seq}`);

// Replay one operator pointer event (normalized [0,1]) on the server-side page.
export const postWebVerifyPointer = (
  sessionId: string,
  kind: "down" | "move" | "up",
  x: number,
  y: number,
): Promise<ApiResponse<string>> =>
  httpPost<string>("/wechat/web-verify/pointer", { sessionId, kind, x, y });

export const wechatRelogin = (
  wcId: string,
  device: DeviceType
): Promise<ApiResponse<QrCodeResult>> =>
  Request.axiosInstance
    .post<ApiResponse<QrCodeResult>>(
      "/wechat/relogin",
      { wcId, deviceType: device },
      { timeout: 60000 }
    )
    .then((res) => res.data);

export const getWechatAccounts = (status = "enabled"): Promise<ApiResponse<WechatAccount[]>> =>
  httpGet<WechatAccount[]>(`/wechat/list?status=${encodeURIComponent(status)}`);

export const setWechatAccountDisabled = (wcId: string, disabled: boolean): Promise<ApiResponse<unknown>> =>
  httpPost<unknown>(`/wechat/${encodeURIComponent(wcId)}/disabled`, { disabled });

export const getWechatUser = (wcId: string): Promise<ApiResponse<WechatAccountDetail>> =>
  httpGet<WechatAccountDetail>(`/wechat/wechat_user/${encodeURIComponent(wcId)}`);

export const wechatRefreshFriendsInfo = (wcId: string): Promise<ApiResponse<unknown>> =>
  httpGet<unknown>(`/wechat/refresh_friends_info/${encodeURIComponent(wcId)}`);

export const wechatSendMessage = (
  wcId: string,
  receiver: string,
  message: string,
  at: string
): Promise<ApiResponse<unknown>> =>
  httpPost<unknown>("/wechat/send_message", { wcId, receiver, message, at });

export const wechatGetChatroomMembers = (
  wId: string,
  chatroomId: string
): Promise<ApiResponse<WechatChatroomMember[]>> =>
  httpPost<WechatChatroomMember[]>("/wechat/chatroom_members", { wId, chatroomId });

// ----- 0.1.43+ send endpoints. Every endpoint returns
// { receivers, jobIds }; clients can poll `wechatGetJob(jobId)` for delivery
// status + upstream identifiers.

export interface SendJobsResult {
  receivers: string[];
  jobIds: string[];
  // Only present on /send_images and /send_files.
  staticFiles?: Array<{ originalName: string; storeName: string }>;
  // Only present on /wechat/forward — the messageType of the resolved
  // source message, so the caller can confirm the dispatch path.
  sourceMessageType?: string;
}

export interface SendJobRecord {
  jobId: string;
  wcId: string;
  receiver: string;
  method: string;
  status: "pending" | "running" | "succeeded" | "failed";
  runAt: string;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  attempts: number;
  result?: { msgId?: number; newMsgId?: number; createTime?: number };
  error?: string;
}

export const wechatSendVoice = (
  wcId: string,
  receiver: string,
  contentUrl: string,
  lengthMs: number,
): Promise<ApiResponse<SendJobsResult>> =>
  httpPost<SendJobsResult>("/wechat/send_voice", { wcId, receiver, contentUrl, lengthMs });

export const wechatSendVideo = (
  wcId: string,
  receiver: string,
  videoPath: string,
  thumbPath: string,
): Promise<ApiResponse<SendJobsResult>> =>
  httpPost<SendJobsResult>("/wechat/send_video", { wcId, receiver, videoPath, thumbPath });

export const wechatSendLink = (
  wcId: string,
  receiver: string,
  link: { title: string; url: string; description?: string; thumbUrl?: string },
): Promise<ApiResponse<SendJobsResult>> =>
  httpPost<SendJobsResult>("/wechat/send_link", { wcId, receiver, ...link });

export const wechatSendApplet = (
  wcId: string,
  receiver: string,
  applet: {
    displayName: string;
    iconUrl: string;
    appId: string;
    pagePath: string;
    thumbUrl: string;
    title: string;
    userName: string;
  },
): Promise<ApiResponse<SendJobsResult>> =>
  httpPost<SendJobsResult>("/wechat/send_applet", { wcId, receiver, ...applet });

export const wechatSendApp = (
  wcId: string,
  receiver: string,
  appXml: string,
): Promise<ApiResponse<SendJobsResult>> =>
  httpPost<SendJobsResult>("/wechat/send_app", { wcId, receiver, appXml });

export const wechatForwardBySource = (
  wcId: string,
  receiver: string,
  source: { sourceNewMsgId?: number; sourceMsgId?: number },
): Promise<ApiResponse<SendJobsResult>> =>
  httpPost<SendJobsResult>("/wechat/forward", { wcId, receiver, ...source });

export const wechatGetJob = (jobId: string): Promise<ApiResponse<SendJobRecord>> =>
  httpGet<SendJobRecord>(`/wechat/job/${encodeURIComponent(jobId)}`);

// ----- chat history -----

export const getMessageContacts = (
  wcId: string,
  refresh = false,
): Promise<ApiResponse<MessageContact[]>> =>
  httpGet<MessageContact[]>(`/message/contacts?wcId=${encodeURIComponent(wcId)}&refresh=${refresh}`);

export const getMessageHistory = (
  wcId: string,
  peerId: string,
  page = 1,
  pageSize = 30
): Promise<ApiResponse<MessageHistoryPage>> => {
  const params = new URLSearchParams({
    wcId,
    peerId,
    page: String(page),
    pageSize: String(pageSize),
  });
  return httpGet<MessageHistoryPage>(`/message/history?${params.toString()}`);
};

export const downloadMessageVideo = (
  wcId: string,
  source: { newMsgId?: number; msgId?: number }
): Promise<ApiResponse<{ url: string }>> =>
  httpPost<{ url: string }>("/message/download_video", { wcId, ...source });

export const getTasks = (): Promise<ApiResponse<Task[]>> => httpGet<Task[]>("/task/list");

export const saveTask = (
  id: string,
  wechatUser: string,
  target: string,
  cron: string,
  content: string,
  at: string
): Promise<ApiResponse<Task>> =>
  httpPost<Task>("/task/save", { id, wechatUser, target, cron, content, at });

export const deleteTask = (id: string): Promise<ApiResponse<unknown>> =>
  httpDelete<unknown>(`/task/delete/${encodeURIComponent(id)}`);

export const getSubscriptions = (): Promise<ApiResponse<SubscriptionRecord[]>> =>
  httpGet<SubscriptionRecord[]>("/subscription/list");

export const saveSubscription = (
  id: string,
  wechatUserId: string,
  fromUsers: Array<string>,
  fromGroups: Array<string>,
  keyword: string,
  at: boolean,
  replyType: number,
  content: string,
  messageTypes: Array<string>
): Promise<ApiResponse<SubscriptionRecord>> =>
  httpPost<SubscriptionRecord>("/subscription/save", {
    id,
    wechatUserId,
    fromUsers,
    fromGroups,
    keyword,
    at,
    replyType,
    content,
    messageTypes,
  });

export const deleteSubscription = (id: string): Promise<ApiResponse<unknown>> =>
  httpDelete<unknown>(`/subscription/delete/${encodeURIComponent(id)}`);

export const validateUser = (ticket: string): Promise<ApiResponse<string>> =>
  httpPost<string>("/user/validate", { ticket });
